import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  DeleteOutline,
  ExpandMore,
  TravelExplore,
} from '@mui/icons-material';
import {
  IBlattAbteilung2Entry,
  IBlattAbteilung3Entry,
  IBlattFlurstueckNeu,
  IBlattGrundstueck,
  IBlattOwner,
  IGrundstueckAddress,
  useGrundbuchblattFullObject,
  useSaveGrundbuchblattFullObject,
} from '../hooks/reactQuerys/GrundbuchblattQueries';
import {
  useAdministrativeUnits,
  useCourtAdministrativeUnitAssignments,
  useCourts,
} from '../Aemter/hooks';
import { BlattOwnersEditor } from './BlattOwnersEditor';
import { BlattBorisLookup } from './BlattBorisLookup';
import { BlattAbrufDialog } from './BlattAbrufDialog';
import { PostcodeSearchByID } from '../generic/PostcodeSearchById';
import { ID_LEGAL_CLIENT_ASSET_ROLE_OWNER } from '../hooks/reactQuerys/AssetQueries';
import { ILegalClient } from '../Interfaces/ILegalClient';
import { ILegalTransactionRelationshipRealEstateOwner } from '../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipRealEstate/ILegalTransactionRelationshipRealEstate';

interface IProps {
  open: boolean;
  /** >0 = bearbeiten, null/undefined = neues Blatt anlegen. */
  idGrundbuchblatt: number | null;
  onClose: () => void;
  onSaved?: (idGrundbuchblatt: number) => void;
}

// Negative Temp-IDs für neue, noch nicht gespeicherte Entitäten (Backend
// behandelt jede id <= 0 als „neu"). Dienen zugleich als stabile React-Keys.
let tempId = -1;
const nextTempId = () => tempId--;

type TabKey = 'bv' | 'abt1' | 'abt2' | 'abt3' | 'abrufe';

/**
 * Optionale Detailfelder eines Grundstücks (#1165). Standardmäßig ausgeblendet;
 * per „+ Weitere Informationen hinzufügen" einblendbar. Felder mit bereits
 * vorhandenem Wert werden automatisch angezeigt. Reihenfolge = Anzeigereihenfolge.
 */
const GRUNDSTUECK_OPTIONAL_FIELDS: {
  key: string;
  label: string;
  hasValue: (gs: IBlattGrundstueck) => boolean;
}[] = [
  {
    key: 'weg',
    label: 'Wohnungs-/Teileigentum',
    hasValue: gs => !!gs.istWohnungseigentum,
  },
  { key: 'erbbau', label: 'Erbbaurecht', hasValue: gs => !!gs.istErbbaurecht },
  {
    key: 'herrschend',
    label: 'Mit dem Eigentum verbundene Rechte',
    hasValue: gs => !!(gs.HerrschendeRechte ?? '').trim(),
  },
  {
    key: 'bestand',
    label: 'Bestand und Zuschreibungen',
    hasValue: gs =>
      !!(gs.Bestandsvermerke ?? '').trim() ||
      !!(gs.BestandsvermerkeRechtspfleger ?? '').trim(),
  },
  {
    key: 'abschreibung',
    label: 'Abschreibungen',
    hasValue: gs =>
      !!(gs.Abschreibungen ?? '').trim() ||
      !!(gs.AbschreibungenRechtspfleger ?? '').trim(),
  },
];

const emptyGrundstueck = (lfdNr: string): IBlattGrundstueck => ({
  idGrundstueck: nextTempId(),
  lfdNr,
  bisherigeLfdNr: '',
  FlurstueckArray: [],
  AddressArray: [],
  Abteilung2Array: [],
  Abteilung3Array: [],
});

/**
 * Blatt-Editor im Grundbuch-Layout (#1108): Kopf (Amtsgericht, Gemarkung,
 * Blattnummer) plus Tabs „Bestandsverzeichnis" und „Abteilung I–III".
 *
 * Bestandsverzeichnis = Grundstücke (lfd. Nr. + bisherige lfd. Nr.) mit ihren
 * Flurstücken (Sachsen: Flur leer) und Adressen. Abteilung I = Eigentümer.
 * Abteilung II/III sind als Gerüst angelegt (Datenmodell folgt). Speichern als
 * Einheit über PUT /grundbuchblatt/fullObject (GrundstueckArray + OwnerArray).
 */
export const GrundbuchblattEditDialog: React.FC<IProps> = ({
  open,
  idGrundbuchblatt,
  onClose,
  onSaved,
}) => {
  const isEdit = !!idGrundbuchblatt && idGrundbuchblatt > 0;
  const { blattFullObject, isLoadingBlattFullObject, refetchBlattFullObject } =
    useGrundbuchblattFullObject(isEdit ? idGrundbuchblatt! : undefined);
  const { saveBlatt, isSavingBlatt } = useSaveGrundbuchblattFullObject();

  const { courts } = useCourts();
  const { administrativeUnits } = useAdministrativeUnits();
  const { courtAdministrativeUnitAssignments } =
    useCourtAdministrativeUnitAssignments();

  const [tab, setTab] = useState<TabKey>('bv');
  const [isOpenAbruf, setIsOpenAbruf] = useState(false);
  const [blattnummer, setBlattnummer] = useState('');
  const [blattart, setBlattart] = useState('');
  const [aufschriftvermerke, setAufschriftvermerke] = useState('');
  const [gemarkungFreitext, setGemarkungFreitext] = useState('');
  const [idCourt, setIdCourt] = useState<number | ''>('');
  const [idAdministrativeUnit, setIdAdministrativeUnit] = useState<number | ''>(
    ''
  );
  const [grundstuecke, setGrundstuecke] = useState<IBlattGrundstueck[]>([]);
  const [owners, setOwners] = useState<IBlattOwner[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Ausklapp-Zustand der „1:1"-Detailfelder je Abteilungs-Eintrag (Key: "2-<id>"/"3-<id>")
  const [abtExpanded, setAbtExpanded] = useState<Record<string, boolean>>({});
  const toggleAbt = (key: string) =>
    setAbtExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  // Beim Öffnen den Zustand aus dem fullObject (oder leer) initialisieren.
  useEffect(() => {
    if (!open) return;
    setTab('bv');
    if (isEdit && blattFullObject) {
      setBlattnummer(blattFullObject.Blattnummer ?? '');
      setBlattart(blattFullObject.Blattart ?? '');
      setAufschriftvermerke(blattFullObject.Aufschriftvermerke ?? '');
      setGemarkungFreitext(blattFullObject.GemarkungFreitext ?? '');
      setIdCourt(blattFullObject.idCourt ?? '');
      setIdAdministrativeUnit(blattFullObject.idAdministrativeUnit ?? '');
      setGrundstuecke(
        (blattFullObject.GrundstueckArray ?? []).map(gs => ({
          ...gs,
          FlurstueckArray: gs.FlurstueckArray ?? [],
          AddressArray: gs.AddressArray ?? [],
          Abteilung2Array: gs.Abteilung2Array ?? [],
          Abteilung3Array: gs.Abteilung3Array ?? [],
        }))
      );
      setOwners(blattFullObject.OwnerArray ?? []);
    } else if (!isEdit) {
      setBlattnummer('');
      setBlattart('');
      setAufschriftvermerke('');
      setGemarkungFreitext('');
      setIdCourt('');
      setIdAdministrativeUnit('');
      setGrundstuecke([emptyGrundstueck('1')]);
      setOwners([]);
    }
    setError(null);
  }, [open, isEdit, blattFullObject]);

  // Gemarkungen, die dem gewählten Amtsgericht zugeordnet sind (Rolle 3).
  const gemarkungOptions = useMemo(() => {
    const all = administrativeUnits ?? [];
    if (!idCourt) return all;
    const assigned = new Set(
      (courtAdministrativeUnitAssignments ?? [])
        .filter(a => a.idCourt === idCourt && a.idCourtRole === 3)
        .map(a => a.idAdministrativeUnit)
    );
    const filtered = all.filter(u => assigned.has(u.idAdministrativeUnit));
    return filtered.length > 0 ? filtered : all;
  }, [administrativeUnits, courtAdministrativeUnitAssignments, idCourt]);

  // Eigentümer-Komponente arbeitet mit dem Vorgangs-Owner-Typ → hin/zurück mappen.
  const ownerBridge: ILegalTransactionRelationshipRealEstateOwner[] =
    owners.map(o => ({
      idLegalClient: o.idLegalClient,
      idLegalClientAssetRole:
        o.idLegalClientAssetRole ?? ID_LEGAL_CLIENT_ASSET_ROLE_OWNER,
      share: o.share ?? null,
    }));
  const setOwnerBridge = (
    next: ILegalTransactionRelationshipRealEstateOwner[]
  ) => {
    setOwners(
      next.map(o => {
        const prev = owners.find(p => p.idLegalClient === o.idLegalClient);
        return {
          ...prev,
          idLegalClient: o.idLegalClient,
          idLegalClientAssetRole: o.idLegalClientAssetRole,
          share: o.share ?? null,
        };
      })
    );
  };
  const knownClients: ILegalClient[] = owners.map(
    o =>
      ({
        idLegalClient: o.idLegalClient,
        FirstName: o.FirstName ?? '',
        LastName: o.LastName ?? '',
        idLegalClientType: o.idLegalClientType ?? 1,
      }) as ILegalClient
  );

  // ---- Grundstück-/Flurstück-/Adress-Mutatoren (immutabel, per id) ----------
  const patchGrundstueck = (gsId: number, patch: Partial<IBlattGrundstueck>) =>
    setGrundstuecke(prev =>
      prev.map(gs => (gs.idGrundstueck === gsId ? { ...gs, ...patch } : gs))
    );

  // #1165: pro Grundstück eingeblendete optionale Felder + „+"-Menü-Anker.
  const [revealedFields, setRevealedFields] = useState<
    Record<number, string[]>
  >({});
  const [addFieldMenu, setAddFieldMenu] = useState<{
    anchorEl: HTMLElement | null;
    gsId: number | null;
  }>({ anchorEl: null, gsId: null });
  const isFieldShown = (gsId: number, key: string, gs: IBlattGrundstueck) => {
    const field = GRUNDSTUECK_OPTIONAL_FIELDS.find(f => f.key === key);
    return (
      (revealedFields[gsId]?.includes(key) ?? false) ||
      (field ? field.hasValue(gs) : false)
    );
  };
  const revealField = (gsId: number, key: string) => {
    setRevealedFields(prev => ({
      ...prev,
      [gsId]: [...(prev[gsId] ?? []), key],
    }));
    setAddFieldMenu({ anchorEl: null, gsId: null });
  };

  // #1160 — Manuelles Umsortieren per Pfeil (hoch/runter). Reine
  // Array-Umsortierung; das Backend leitet die persistente Position aus der
  // Reihenfolge beim Speichern ab.
  const moveInArray = <T,>(arr: T[], index: number, dir: -1 | 1): T[] => {
    const to = index + dir;
    if (to < 0 || to >= arr.length) return arr;
    const next = [...arr];
    [next[index], next[to]] = [next[to], next[index]];
    return next;
  };
  const moveGrundstueck = (index: number, dir: -1 | 1) =>
    setGrundstuecke(prev => moveInArray(prev, index, dir));
  const moveInGrundstueckList = (
    gsId: number,
    key: 'FlurstueckArray' | 'Abteilung2Array' | 'Abteilung3Array',
    index: number,
    dir: -1 | 1
  ) =>
    setGrundstuecke(prev =>
      prev.map(gs =>
        gs.idGrundstueck === gsId
          ? {
              ...gs,
              [key]: moveInArray([...((gs[key] as any[]) ?? [])], index, dir),
            }
          : gs
      )
    );
  const renderMoveArrows = (
    index: number,
    length: number,
    onMove: (dir: -1 | 1) => void
  ) => (
    <Stack direction='row'>
      <Tooltip title='Nach oben'>
        <span>
          <IconButton
            size='small'
            disabled={index <= 0}
            onClick={() => onMove(-1)}
          >
            <ArrowUpward fontSize='inherit' />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title='Nach unten'>
        <span>
          <IconButton
            size='small'
            disabled={index >= length - 1}
            onClick={() => onMove(1)}
          >
            <ArrowDownward fontSize='inherit' />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );

  const addGrundstueck = () =>
    setGrundstuecke(prev => [
      ...prev,
      emptyGrundstueck(String(prev.length + 1)),
    ]);

  const removeGrundstueck = (gsId: number) =>
    setGrundstuecke(prev => prev.filter(gs => gs.idGrundstueck !== gsId));

  const addFlurstueck = (gsId: number) =>
    setGrundstuecke(prev =>
      prev.map(gs =>
        gs.idGrundstueck === gsId
          ? {
              ...gs,
              FlurstueckArray: [
                ...gs.FlurstueckArray,
                {
                  idFlurstueck: nextTempId(),
                  Flur: '',
                  Flurstuecksnummer: '',
                  idAdministrativeUnit:
                    idAdministrativeUnit === '' ? null : idAdministrativeUnit,
                  Wirtschaftsart: '',
                  Lage: '',
                  Groesse: null,
                },
              ],
            }
          : gs
      )
    );

  const updateFlurstueck = (
    gsId: number,
    fId: number,
    field: keyof IBlattFlurstueckNeu,
    value: string | number | null
  ) =>
    setGrundstuecke(prev =>
      prev.map(gs =>
        gs.idGrundstueck === gsId
          ? {
              ...gs,
              FlurstueckArray: gs.FlurstueckArray.map(f =>
                f.idFlurstueck === fId ? { ...f, [field]: value } : f
              ),
            }
          : gs
      )
    );

  const removeFlurstueck = (gsId: number, fId: number) =>
    setGrundstuecke(prev =>
      prev.map(gs =>
        gs.idGrundstueck === gsId
          ? {
              ...gs,
              FlurstueckArray: gs.FlurstueckArray.filter(
                f => f.idFlurstueck !== fId
              ),
            }
          : gs
      )
    );

  // Flurstück aus einem BORIS-Treffer (Brandenburg) als neue Zeile übernehmen.
  const addFlurstueckFromBoris = (
    gsId: number,
    patch: Partial<IBlattFlurstueckNeu>
  ) =>
    setGrundstuecke(prev =>
      prev.map(gs =>
        gs.idGrundstueck === gsId
          ? {
              ...gs,
              FlurstueckArray: [
                ...gs.FlurstueckArray,
                {
                  idFlurstueck: nextTempId(),
                  Flur: patch.Flur ?? '',
                  Flurstuecksnummer: patch.Flurstuecksnummer ?? '',
                  idAdministrativeUnit:
                    patch.idAdministrativeUnit ??
                    (idAdministrativeUnit === '' ? null : idAdministrativeUnit),
                  Wirtschaftsart: patch.Wirtschaftsart ?? '',
                  Lage: patch.Lage ?? '',
                  Groesse: patch.Groesse ?? null,
                },
              ],
            }
          : gs
      )
    );

  const addAddress = (gsId: number) =>
    setGrundstuecke(prev =>
      prev.map(gs =>
        gs.idGrundstueck === gsId
          ? {
              ...gs,
              AddressArray: [
                ...gs.AddressArray,
                {
                  idAddress: nextTempId(),
                  Street: '',
                  StreetNr: '',
                  idPostcode: null,
                },
              ],
            }
          : gs
      )
    );

  const updateAddress = (
    gsId: number,
    aId: number,
    field: keyof IGrundstueckAddress,
    value: string | number | null
  ) =>
    setGrundstuecke(prev =>
      prev.map(gs =>
        gs.idGrundstueck === gsId
          ? {
              ...gs,
              AddressArray: gs.AddressArray.map(a =>
                a.idAddress === aId ? { ...a, [field]: value } : a
              ),
            }
          : gs
      )
    );

  const removeAddress = (gsId: number, aId: number) =>
    setGrundstuecke(prev =>
      prev.map(gs =>
        gs.idGrundstueck === gsId
          ? {
              ...gs,
              AddressArray: gs.AddressArray.filter(a => a.idAddress !== aId),
            }
          : gs
      )
    );

  // ---- Abteilung II ----------------------------------------------------------
  const addAbt2 = (gsId: number) =>
    setGrundstuecke(prev =>
      prev.map(gs =>
        gs.idGrundstueck === gsId
          ? {
              ...gs,
              Abteilung2Array: [
                ...(gs.Abteilung2Array ?? []),
                {
                  idGrundbuchAbteilung2: nextTempId(),
                  lfdNr: '',
                  Belastungsart: '',
                  Berechtigter: '',
                  Inhalt: '',
                  Betrag: null,
                  eingetragenAm: '',
                  isVormerkung: false,
                },
              ],
            }
          : gs
      )
    );
  const updateAbt2 = (
    gsId: number,
    eId: number,
    field: keyof IBlattAbteilung2Entry,
    value: string | number | boolean | null
  ) =>
    setGrundstuecke(prev =>
      prev.map(gs =>
        gs.idGrundstueck === gsId
          ? {
              ...gs,
              Abteilung2Array: (gs.Abteilung2Array ?? []).map(e =>
                e.idGrundbuchAbteilung2 === eId ? { ...e, [field]: value } : e
              ),
            }
          : gs
      )
    );
  const removeAbt2 = (gsId: number, eId: number) =>
    setGrundstuecke(prev =>
      prev.map(gs =>
        gs.idGrundstueck === gsId
          ? {
              ...gs,
              Abteilung2Array: (gs.Abteilung2Array ?? []).filter(
                e => e.idGrundbuchAbteilung2 !== eId
              ),
            }
          : gs
      )
    );

  // ---- Abteilung III ---------------------------------------------------------
  const addAbt3 = (gsId: number) =>
    setGrundstuecke(prev =>
      prev.map(gs =>
        gs.idGrundstueck === gsId
          ? {
              ...gs,
              Abteilung3Array: [
                ...(gs.Abteilung3Array ?? []),
                {
                  idGrundbuchAbteilung3: nextTempId(),
                  lfdNr: '',
                  Belastungsart: '',
                  Betrag: null,
                  Waehrung: 'EUR',
                  Glaeubiger: '',
                  Zinssatz: null,
                  eingetragenAm: '',
                  isOhneBrief: false,
                  isVollstreckbar: false,
                },
              ],
            }
          : gs
      )
    );
  const updateAbt3 = (
    gsId: number,
    eId: number,
    field: keyof IBlattAbteilung3Entry,
    value: string | number | boolean | null
  ) =>
    setGrundstuecke(prev =>
      prev.map(gs =>
        gs.idGrundstueck === gsId
          ? {
              ...gs,
              Abteilung3Array: (gs.Abteilung3Array ?? []).map(e =>
                e.idGrundbuchAbteilung3 === eId ? { ...e, [field]: value } : e
              ),
            }
          : gs
      )
    );
  const removeAbt3 = (gsId: number, eId: number) =>
    setGrundstuecke(prev =>
      prev.map(gs =>
        gs.idGrundstueck === gsId
          ? {
              ...gs,
              Abteilung3Array: (gs.Abteilung3Array ?? []).filter(
                e => e.idGrundbuchAbteilung3 !== eId
              ),
            }
          : gs
      )
    );

  const flurstueckGesamt = grundstuecke.reduce(
    (n, gs) => n + gs.FlurstueckArray.length,
    0
  );
  const abt2Gesamt = grundstuecke.reduce(
    (n, gs) => n + (gs.Abteilung2Array?.length ?? 0),
    0
  );
  const abt3Gesamt = grundstuecke.reduce(
    (n, gs) => n + (gs.Abteilung3Array?.length ?? 0),
    0
  );

  // Grundstück-Optionen (lfd. Nr.) für die Referenz-Dropdowns in Abt. I–III.
  const grundstueckOptions = grundstuecke.map(gs => ({
    id: gs.idGrundstueck as number,
    lfdNr: gs.lfdNr ?? '',
  }));

  // Abt.-Eintrag einem anderen Grundstück zuordnen (Referenz ändern).
  const moveAbt2 = (fromGsId: number, eId: number, toGsId: number) => {
    if (fromGsId === toGsId) return;
    setGrundstuecke(prev => {
      let moved: IBlattAbteilung2Entry | undefined;
      const stripped = prev.map(gs => {
        if (gs.idGrundstueck !== fromGsId) return gs;
        const arr = gs.Abteilung2Array ?? [];
        moved = arr.find(e => e.idGrundbuchAbteilung2 === eId);
        return {
          ...gs,
          Abteilung2Array: arr.filter(e => e.idGrundbuchAbteilung2 !== eId),
        };
      });
      if (!moved) return prev;
      const entry = moved;
      return stripped.map(gs =>
        gs.idGrundstueck === toGsId
          ? { ...gs, Abteilung2Array: [...(gs.Abteilung2Array ?? []), entry] }
          : gs
      );
    });
  };
  const moveAbt3 = (fromGsId: number, eId: number, toGsId: number) => {
    if (fromGsId === toGsId) return;
    setGrundstuecke(prev => {
      let moved: IBlattAbteilung3Entry | undefined;
      const stripped = prev.map(gs => {
        if (gs.idGrundstueck !== fromGsId) return gs;
        const arr = gs.Abteilung3Array ?? [];
        moved = arr.find(e => e.idGrundbuchAbteilung3 === eId);
        return {
          ...gs,
          Abteilung3Array: arr.filter(e => e.idGrundbuchAbteilung3 !== eId),
        };
      });
      if (!moved) return prev;
      const entry = moved;
      return stripped.map(gs =>
        gs.idGrundstueck === toGsId
          ? { ...gs, Abteilung3Array: [...(gs.Abteilung3Array ?? []), entry] }
          : gs
      );
    });
  };

  const handleSave = async () => {
    if (!blattnummer.trim()) {
      setError('Bitte eine Blattnummer angeben.');
      setTab('bv');
      return;
    }
    setError(null);
    try {
      const saved = await saveBlatt({
        idGrundbuchblatt: isEdit ? idGrundbuchblatt! : nextTempId(),
        Blattnummer: blattnummer.trim(),
        Blattart: blattart.trim() || null,
        Aufschriftvermerke: aufschriftvermerke.trim() || null,
        GemarkungFreitext:
          idAdministrativeUnit === '' ? gemarkungFreitext.trim() || null : null,
        idCourt: idCourt === '' ? null : idCourt,
        idAdministrativeUnit:
          idAdministrativeUnit === '' ? null : idAdministrativeUnit,
        GrundstueckArray: grundstuecke,
        OwnerArray: owners,
      });
      if (saved?.fehler) {
        setError(saved.fehler);
        return;
      }
      onSaved?.(saved?.idGrundbuchblatt ?? idGrundbuchblatt ?? 0);
      onClose();
    } catch (e) {
      setError('Speichern fehlgeschlagen. Bitte erneut versuchen.');
    }
  };

  const renderGrundstueck = (gs: IBlattGrundstueck, index: number) => {
    const gsId = gs.idGrundstueck as number;
    return (
      <Paper key={gsId} variant='outlined' sx={{ p: 2 }}>
        <Stack direction='row' alignItems='center' spacing={2} sx={{ mb: 1 }}>
          <Typography variant='subtitle2' sx={{ whiteSpace: 'nowrap' }}>
            Grundstück
          </Typography>
          <TextField
            label='Lfd. Nr.'
            value={gs.lfdNr ?? ''}
            onChange={e => patchGrundstueck(gsId, { lfdNr: e.target.value })}
            size='small'
            sx={{ width: 110 }}
          />
          <TextField
            label='Bisherige lfd. Nr.'
            value={gs.bisherigeLfdNr ?? ''}
            onChange={e =>
              patchGrundstueck(gsId, { bisherigeLfdNr: e.target.value })
            }
            size='small'
            sx={{ width: 170 }}
            helperText='Vorgänger (z. B. „2, 3")'
          />
          <Box sx={{ flexGrow: 1 }} />
          {renderMoveArrows(index, grundstuecke.length, dir =>
            moveGrundstueck(index, dir)
          )}
          <Tooltip title='Grundstück entfernen'>
            <span>
              <IconButton
                size='small'
                color='error'
                disabled={grundstuecke.length <= 1}
                onClick={() => removeGrundstueck(gsId)}
              >
                <DeleteOutline fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        {/* Optionale Detailfelder — standardmäßig ausgeblendet, per „+" einblendbar (#1165) */}
        <Box
          sx={theme => ({
            mb: 1.5,
            p: 1.5,
            borderRadius: 1,
            border: `1px dashed ${theme.palette.divider}`,
          })}
        >
          {isFieldShown(gsId, 'weg', gs) && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    size='small'
                    checked={!!gs.istWohnungseigentum}
                    onChange={(_, v) =>
                      patchGrundstueck(gsId, { istWohnungseigentum: v ? 1 : 0 })
                    }
                  />
                }
                label='Wohnungs-/Teileigentum'
              />
              {!!gs.istWohnungseigentum && (
                <Stack spacing={1} sx={{ mt: 0.5 }}>
                  <Stack
                    direction='row'
                    spacing={1}
                    alignItems='center'
                    flexWrap='wrap'
                    useFlexGap
                  >
                    <Typography variant='caption' color='text.secondary'>
                      Miteigentumsanteil
                    </Typography>
                    <TextField
                      size='small'
                      type='number'
                      label='Zähler'
                      sx={{ width: 110 }}
                      value={gs.MiteigentumsanteilZaehler ?? ''}
                      onChange={e =>
                        patchGrundstueck(gsId, {
                          MiteigentumsanteilZaehler:
                            e.target.value === ''
                              ? null
                              : Number(e.target.value),
                        })
                      }
                    />
                    <Typography>/</Typography>
                    <TextField
                      size='small'
                      type='number'
                      label='Nenner'
                      sx={{ width: 110 }}
                      value={gs.MiteigentumsanteilNenner ?? ''}
                      onChange={e =>
                        patchGrundstueck(gsId, {
                          MiteigentumsanteilNenner:
                            e.target.value === ''
                              ? null
                              : Number(e.target.value),
                        })
                      }
                    />
                    <TextField
                      size='small'
                      label='Aufteilungsplan-Nr.'
                      sx={{ width: 160 }}
                      value={gs.AufteilungsplanNr ?? ''}
                      onChange={e =>
                        patchGrundstueck(gsId, {
                          AufteilungsplanNr: e.target.value,
                        })
                      }
                    />
                  </Stack>
                  <TextField
                    size='small'
                    fullWidth
                    multiline
                    label='Sondereigentum'
                    placeholder='z. B. Sondereigentum an der Wohnung Nr. 5 im Dachgeschoss lt. Aufteilungsplan'
                    value={gs.SondereigentumText ?? ''}
                    onChange={e =>
                      patchGrundstueck(gsId, {
                        SondereigentumText: e.target.value,
                      })
                    }
                  />
                  <TextField
                    size='small'
                    fullWidth
                    multiline
                    label='Teilungserklärung (Bezug)'
                    value={gs.TeilungserklaerungBezug ?? ''}
                    onChange={e =>
                      patchGrundstueck(gsId, {
                        TeilungserklaerungBezug: e.target.value,
                      })
                    }
                  />
                  <TextField
                    size='small'
                    fullWidth
                    multiline
                    label='Veräußerungsbeschränkung (z. B. Verwalterzustimmung)'
                    value={gs.Veraeusserungsbeschraenkung ?? ''}
                    onChange={e =>
                      patchGrundstueck(gsId, {
                        Veraeusserungsbeschraenkung: e.target.value,
                      })
                    }
                  />
                </Stack>
              )}
            </>
          )}
          {isFieldShown(gsId, 'erbbau', gs) && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    size='small'
                    checked={!!gs.istErbbaurecht}
                    onChange={(_, v) =>
                      patchGrundstueck(gsId, { istErbbaurecht: v ? 1 : 0 })
                    }
                  />
                }
                label='Erbbaurecht'
              />
              {!!gs.istErbbaurecht && (
                <Stack spacing={1} sx={{ mt: 0.5 }}>
                  <TextField
                    size='small'
                    fullWidth
                    label='Erbbauzins'
                    placeholder='z. B. 3 % des Grundstückswerts p. a.'
                    value={gs.ErbbauzinsText ?? ''}
                    onChange={e =>
                      patchGrundstueck(gsId, { ErbbauzinsText: e.target.value })
                    }
                  />
                  <TextField
                    size='small'
                    label='Laufzeit bis'
                    placeholder='z. B. 2098'
                    sx={{ width: 160 }}
                    value={gs.ErbbaurechtLaufzeitBis ?? ''}
                    onChange={e =>
                      patchGrundstueck(gsId, {
                        ErbbaurechtLaufzeitBis: e.target.value,
                      })
                    }
                  />
                  <TextField
                    size='small'
                    fullWidth
                    multiline
                    label='Bezug (Bestellungs-/Erbbaurechtsvertrag)'
                    value={gs.ErbbaurechtBezug ?? ''}
                    onChange={e =>
                      patchGrundstueck(gsId, {
                        ErbbaurechtBezug: e.target.value,
                      })
                    }
                  />
                </Stack>
              )}
            </>
          )}
          {isFieldShown(gsId, 'herrschend', gs) && (
            <TextField
              size='small'
              fullWidth
              multiline
              sx={{ mt: 1 }}
              label='Mit dem Eigentum verbundene Rechte (herrschend)'
              value={gs.HerrschendeRechte ?? ''}
              onChange={e =>
                patchGrundstueck(gsId, { HerrschendeRechte: e.target.value })
              }
            />
          )}
          {isFieldShown(gsId, 'bestand', gs) && (
            <>
              <TextField
                size='small'
                fullWidth
                multiline
                sx={{ mt: 1 }}
                label='Bestand und Zuschreibungen (Spalte 6)'
                placeholder='z. B. „Von Blatt … hierher übertragen am …"'
                value={gs.Bestandsvermerke ?? ''}
                onChange={e =>
                  patchGrundstueck(gsId, { Bestandsvermerke: e.target.value })
                }
              />
              <TextField
                size='small'
                sx={{ mt: 1, width: 360 }}
                label='Rechtspfleger (Bestand / Zuschreibungen)'
                value={gs.BestandsvermerkeRechtspfleger ?? ''}
                onChange={e =>
                  patchGrundstueck(gsId, {
                    BestandsvermerkeRechtspfleger: e.target.value,
                  })
                }
              />
            </>
          )}
          {isFieldShown(gsId, 'abschreibung', gs) && (
            <>
              <TextField
                size='small'
                fullWidth
                multiline
                sx={{ mt: 1 }}
                label='Abschreibungen (Spalte 8)'
                placeholder='z. B. „Nach Blatt … abgeschrieben am …"'
                value={gs.Abschreibungen ?? ''}
                onChange={e =>
                  patchGrundstueck(gsId, { Abschreibungen: e.target.value })
                }
              />
              <TextField
                size='small'
                sx={{ mt: 1, width: 360 }}
                label='Rechtspfleger (Abschreibungen)'
                value={gs.AbschreibungenRechtspfleger ?? ''}
                onChange={e =>
                  patchGrundstueck(gsId, {
                    AbschreibungenRechtspfleger: e.target.value,
                  })
                }
              />
            </>
          )}
          {/* #1165: weitere (optionale) Felder gezielt einblenden */}
          <Box sx={{ mt: 1 }}>
            <Tooltip title='Weitere Informationen hinzufügen'>
              <span>
                <Button
                  size='small'
                  startIcon={<Add />}
                  disabled={GRUNDSTUECK_OPTIONAL_FIELDS.every(f =>
                    isFieldShown(gsId, f.key, gs)
                  )}
                  onClick={e =>
                    setAddFieldMenu({ anchorEl: e.currentTarget, gsId })
                  }
                >
                  Weitere Informationen hinzufügen
                </Button>
              </span>
            </Tooltip>
            <Menu
              anchorEl={addFieldMenu.anchorEl}
              open={addFieldMenu.gsId === gsId && !!addFieldMenu.anchorEl}
              onClose={() => setAddFieldMenu({ anchorEl: null, gsId: null })}
            >
              {GRUNDSTUECK_OPTIONAL_FIELDS.filter(
                f => !isFieldShown(gsId, f.key, gs)
              ).map(f => (
                <MenuItem key={f.key} onClick={() => revealField(gsId, f.key)}>
                  {f.label}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Box>

        {/* Flurstücke */}
        <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 0.5 }}>
          <Typography variant='body2' color='text.secondary'>
            Flur / Flurstücke
          </Typography>
          <Chip
            size='small'
            label={gs.FlurstueckArray.length}
            variant='outlined'
          />
          <Box sx={{ flexGrow: 1 }} />
          <Button
            size='small'
            startIcon={<Add />}
            onClick={() => addFlurstueck(gsId)}
          >
            Flurstück
          </Button>
        </Stack>
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ display: 'block', mb: 0.5 }}
        >
          Hinweis: Die Nachverfolgung (Aktualitätsnachweis) bezieht sich auf das
          gesamte Grundbuchblatt, nicht auf einzelne Flurstücke.
        </Typography>
        {gs.FlurstueckArray.length === 0 ? (
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
            Noch keine Flurstücke.
          </Typography>
        ) : (
          <Table size='small' sx={{ mb: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 90 }}>Flur</TableCell>
                <TableCell sx={{ width: 120 }}>Flurstück</TableCell>
                <TableCell>Lage</TableCell>
                <TableCell>Wirtschaftsart</TableCell>
                <TableCell sx={{ width: 110 }}>Größe (m²)</TableCell>
                <TableCell sx={{ width: 40 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {gs.FlurstueckArray.map((f, fIdx) => {
                const fId = f.idFlurstueck as number;
                return (
                  <TableRow key={fId}>
                    <TableCell>
                      <TextField
                        variant='standard'
                        value={f.Flur ?? ''}
                        onChange={e =>
                          updateFlurstueck(gsId, fId, 'Flur', e.target.value)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant='standard'
                        value={f.Flurstuecksnummer ?? ''}
                        onChange={e =>
                          updateFlurstueck(
                            gsId,
                            fId,
                            'Flurstuecksnummer',
                            e.target.value
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant='standard'
                        fullWidth
                        placeholder='z. B. Kleine Stücke, Musterstr. 1'
                        value={f.Lage ?? ''}
                        onChange={e =>
                          updateFlurstueck(gsId, fId, 'Lage', e.target.value)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant='standard'
                        fullWidth
                        placeholder='z. B. Gebäude- und Freifläche'
                        value={f.Wirtschaftsart ?? ''}
                        onChange={e =>
                          updateFlurstueck(
                            gsId,
                            fId,
                            'Wirtschaftsart',
                            e.target.value
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant='standard'
                        type='number'
                        value={f.Groesse ?? ''}
                        onChange={e =>
                          updateFlurstueck(
                            gsId,
                            fId,
                            'Groesse',
                            e.target.value === ''
                              ? null
                              : Number(e.target.value)
                          )
                        }
                      />
                    </TableCell>
                    <TableCell align='right'>
                      <Stack
                        direction='row'
                        justifyContent='flex-end'
                        alignItems='center'
                      >
                        {renderMoveArrows(
                          fIdx,
                          gs.FlurstueckArray.length,
                          dir =>
                            moveInGrundstueckList(
                              gsId,
                              'FlurstueckArray',
                              fIdx,
                              dir
                            )
                        )}
                        <Tooltip title='Flurstück entfernen'>
                          <IconButton
                            size='small'
                            onClick={() => removeFlurstueck(gsId, fId)}
                          >
                            <DeleteOutline fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {/* BORIS (Brandenburg): Kataster + Bodenrichtwert zur Adresse */}
        <BlattBorisLookup
          defaultAddress={(() => {
            const a = (gs.AddressArray ?? [])[0];
            if (!a) return '';
            const l1 = [a.Street, a.StreetNr].filter(Boolean).join(' ').trim();
            const l2 = [a.ZipCode, a.Place].filter(Boolean).join(' ').trim();
            return [l1, l2].filter(Boolean).join(', ');
          })()}
          courtAdminAssignments={courtAdministrativeUnitAssignments ?? []}
          onApply={patch => addFlurstueckFromBoris(gsId, patch)}
        />

        {/* Adressen des Grundstücks */}
        <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 0.5 }}>
          <Typography variant='body2' color='text.secondary'>
            Adressen
          </Typography>
          <Chip
            size='small'
            label={gs.AddressArray.length}
            variant='outlined'
          />
          <Box sx={{ flexGrow: 1 }} />
          <Button
            size='small'
            startIcon={<Add />}
            onClick={() => addAddress(gsId)}
          >
            Adresse
          </Button>
        </Stack>
        {gs.AddressArray.length === 0 ? (
          <Typography variant='body2' color='text.secondary'>
            Keine Adressen.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {gs.AddressArray.map(a => {
              const aId = a.idAddress as number;
              return (
                <Stack
                  key={aId}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ sm: 'center' }}
                >
                  <TextField
                    label='Straße'
                    size='small'
                    value={a.Street ?? ''}
                    onChange={e =>
                      updateAddress(gsId, aId, 'Street', e.target.value)
                    }
                    sx={{ flex: 2 }}
                  />
                  <TextField
                    label='Nr.'
                    size='small'
                    value={a.StreetNr ?? ''}
                    onChange={e =>
                      updateAddress(gsId, aId, 'StreetNr', e.target.value)
                    }
                    sx={{ width: 90 }}
                  />
                  <Box sx={{ flex: 2 }}>
                    <PostcodeSearchByID
                      idPostcode={a.idPostcode ?? null}
                      setIdPostcode={(id: number | null) =>
                        updateAddress(gsId, aId, 'idPostcode', id ?? null)
                      }
                      required={false}
                    />
                  </Box>
                  <Tooltip title='Adresse entfernen'>
                    <IconButton
                      size='small'
                      onClick={() => removeAddress(gsId, aId)}
                    >
                      <DeleteOutline fontSize='small' />
                    </IconButton>
                  </Tooltip>
                </Stack>
              );
            })}
          </Stack>
        )}
      </Paper>
    );
  };

  const renderAbteilung2 = () => (
    <Stack spacing={2}>
      <Typography variant='body2' color='text.secondary'>
        Lasten und Beschränkungen je Grundstück (Bezug über die laufende
        Nummer).
      </Typography>
      {grundstuecke.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>
          Erst ein Grundstück im Bestandsverzeichnis anlegen.
        </Typography>
      ) : (
        grundstuecke.map(gs => {
          const gsId = gs.idGrundstueck as number;
          const entries = gs.Abteilung2Array ?? [];
          return (
            <Paper key={gsId} variant='outlined' sx={{ p: 2 }}>
              <Stack
                direction='row'
                alignItems='center'
                spacing={1}
                sx={{ mb: 1 }}
              >
                <Typography variant='subtitle2'>
                  Grundstück · Lfd. Nr. {gs.lfdNr ?? '—'}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  size='small'
                  startIcon={<Add />}
                  onClick={() => addAbt2(gsId)}
                >
                  Eintrag
                </Button>
              </Stack>
              {entries.length === 0 ? (
                <Typography variant='body2' color='text.secondary'>
                  Keine Eintragungen in Abteilung II.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {entries.map((e, eIdx) => {
                    const eId = e.idGrundbuchAbteilung2 as number;
                    return (
                      <Paper
                        key={eId}
                        variant='outlined'
                        sx={{
                          p: 1.5,
                          bgcolor: 'action.hover',
                          opacity: e.istGeloescht ? 0.6 : 1,
                        }}
                      >
                        <Box
                          sx={{ display: 'flex', justifyContent: 'flex-end' }}
                        >
                          {renderMoveArrows(eIdx, entries.length, dir =>
                            moveInGrundstueckList(
                              gsId,
                              'Abteilung2Array',
                              eIdx,
                              dir
                            )
                          )}
                        </Box>
                        <Stack
                          direction='row'
                          spacing={1.5}
                          flexWrap='wrap'
                          useFlexGap
                          alignItems='flex-start'
                        >
                          <TextField
                            select
                            size='small'
                            label='Grundstück'
                            value={gsId}
                            onChange={ev =>
                              moveAbt2(gsId, eId, Number(ev.target.value))
                            }
                            sx={{ width: 120 }}
                          >
                            {grundstueckOptions.map(g => (
                              <MenuItem key={g.id} value={g.id}>
                                Lfd. Nr. {g.lfdNr}
                              </MenuItem>
                            ))}
                          </TextField>
                          <TextField
                            size='small'
                            label='Lfd. Nr.'
                            value={e.lfdNr ?? ''}
                            onChange={ev =>
                              updateAbt2(gsId, eId, 'lfdNr', ev.target.value)
                            }
                            sx={{ width: 80 }}
                          />
                          <TextField
                            size='small'
                            label='Belastungsart'
                            placeholder='z. B. Grunddienstbarkeit'
                            value={e.Belastungsart ?? ''}
                            onChange={ev =>
                              updateAbt2(
                                gsId,
                                eId,
                                'Belastungsart',
                                ev.target.value
                              )
                            }
                            sx={{ flexGrow: 1, minWidth: 220 }}
                          />
                          <TextField
                            size='small'
                            label='Berechtigter'
                            value={e.Berechtigter ?? ''}
                            onChange={ev =>
                              updateAbt2(
                                gsId,
                                eId,
                                'Berechtigter',
                                ev.target.value
                              )
                            }
                            sx={{ flexGrow: 1, minWidth: 220 }}
                          />
                          <TextField
                            size='small'
                            label='Betrag (€)'
                            type='number'
                            value={e.Betrag ?? ''}
                            onChange={ev =>
                              updateAbt2(
                                gsId,
                                eId,
                                'Betrag',
                                ev.target.value === ''
                                  ? null
                                  : Number(ev.target.value)
                              )
                            }
                            sx={{ width: 130 }}
                          />
                          <TextField
                            size='small'
                            label='Eingetragen am'
                            type='date'
                            InputLabelProps={{ shrink: true }}
                            value={e.eingetragenAm ?? ''}
                            onChange={ev =>
                              updateAbt2(
                                gsId,
                                eId,
                                'eingetragenAm',
                                ev.target.value
                              )
                            }
                            sx={{ width: 160 }}
                          />
                          <FormControlLabel
                            sx={{ mt: 0.5 }}
                            control={
                              <Checkbox
                                size='small'
                                checked={!!e.isVormerkung}
                                onChange={ev =>
                                  updateAbt2(
                                    gsId,
                                    eId,
                                    'isVormerkung',
                                    ev.target.checked
                                  )
                                }
                              />
                            }
                            label='Vormerkung'
                          />
                          <Box sx={{ flexGrow: 1 }} />
                          <Tooltip title='1:1-Details (Volltext, Rang, Gesamthaft, Veränderungen, Rechtspfleger, Löschung)'>
                            <IconButton
                              size='small'
                              onClick={() => toggleAbt(`2-${eId}`)}
                            >
                              <ExpandMore
                                fontSize='small'
                                sx={{
                                  transform: abtExpanded[`2-${eId}`]
                                    ? 'rotate(180deg)'
                                    : 'none',
                                  transition: '0.2s',
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title='Eintrag entfernen'>
                            <IconButton
                              size='small'
                              onClick={() => removeAbt2(gsId, eId)}
                            >
                              <DeleteOutline fontSize='small' />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        <TextField
                          size='small'
                          fullWidth
                          label='Inhalt'
                          value={e.Inhalt ?? ''}
                          onChange={ev =>
                            updateAbt2(gsId, eId, 'Inhalt', ev.target.value)
                          }
                          sx={{ mt: 1.5 }}
                        />
                        <Collapse in={!!abtExpanded[`2-${eId}`]} unmountOnExit>
                          <Stack spacing={1} sx={{ pt: 1.5 }}>
                            <TextField
                              size='small'
                              fullWidth
                              multiline
                              label='Volltext (wörtlich, Spalte 3)'
                              value={e.Volltext ?? ''}
                              onChange={ev =>
                                updateAbt2(
                                  gsId,
                                  eId,
                                  'Volltext',
                                  ev.target.value
                                )
                              }
                            />
                            <Stack
                              direction='row'
                              spacing={1}
                              flexWrap='wrap'
                              useFlexGap
                            >
                              <TextField
                                size='small'
                                label='Rangvermerk'
                                placeholder='im Rang vor/nach …'
                                value={e.Rangvermerk ?? ''}
                                onChange={ev =>
                                  updateAbt2(
                                    gsId,
                                    eId,
                                    'Rangvermerk',
                                    ev.target.value
                                  )
                                }
                                sx={{ flexGrow: 1, minWidth: 220 }}
                              />
                              <TextField
                                size='small'
                                label='Gesamthaft (Blätter)'
                                placeholder='z. B. 10558, 10573'
                                value={e.GesamthaftBlaetter ?? ''}
                                onChange={ev =>
                                  updateAbt2(
                                    gsId,
                                    eId,
                                    'GesamthaftBlaetter',
                                    ev.target.value
                                  )
                                }
                                sx={{ width: 200 }}
                              />
                            </Stack>
                            <TextField
                              size='small'
                              fullWidth
                              multiline
                              label='Veränderungen'
                              value={e.Veraenderungen ?? ''}
                              onChange={ev =>
                                updateAbt2(
                                  gsId,
                                  eId,
                                  'Veraenderungen',
                                  ev.target.value
                                )
                              }
                            />
                            <TextField
                              size='small'
                              fullWidth
                              label='Rechtspfleger (Unterzeichner)'
                              value={e.Rechtspfleger ?? ''}
                              onChange={ev =>
                                updateAbt2(
                                  gsId,
                                  eId,
                                  'Rechtspfleger',
                                  ev.target.value
                                )
                              }
                            />
                            <Stack
                              direction='row'
                              spacing={1}
                              alignItems='center'
                            >
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size='small'
                                    checked={!!e.istGeloescht}
                                    onChange={ev =>
                                      updateAbt2(
                                        gsId,
                                        eId,
                                        'istGeloescht',
                                        ev.target.checked
                                      )
                                    }
                                  />
                                }
                                label='gelöscht (historisch)'
                              />
                              {e.istGeloescht ? (
                                <TextField
                                  size='small'
                                  type='date'
                                  label='Gelöscht am'
                                  InputLabelProps={{ shrink: true }}
                                  value={e.geloeschtAm ?? ''}
                                  onChange={ev =>
                                    updateAbt2(
                                      gsId,
                                      eId,
                                      'geloeschtAm',
                                      ev.target.value
                                    )
                                  }
                                  sx={{ width: 180 }}
                                />
                              ) : null}
                            </Stack>
                          </Stack>
                        </Collapse>
                      </Paper>
                    );
                  })}
                </Stack>
              )}
            </Paper>
          );
        })
      )}
    </Stack>
  );

  const renderAbteilung3 = () => (
    <Stack spacing={2}>
      <Typography variant='body2' color='text.secondary'>
        Grundpfandrechte (Grundschuld, Hypothek …) je Grundstück.
      </Typography>
      {grundstuecke.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>
          Erst ein Grundstück im Bestandsverzeichnis anlegen.
        </Typography>
      ) : (
        grundstuecke.map(gs => {
          const gsId = gs.idGrundstueck as number;
          const entries = gs.Abteilung3Array ?? [];
          return (
            <Paper key={gsId} variant='outlined' sx={{ p: 2 }}>
              <Stack
                direction='row'
                alignItems='center'
                spacing={1}
                sx={{ mb: 1 }}
              >
                <Typography variant='subtitle2'>
                  Grundstück · Lfd. Nr. {gs.lfdNr ?? '—'}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  size='small'
                  startIcon={<Add />}
                  onClick={() => addAbt3(gsId)}
                >
                  Eintrag
                </Button>
              </Stack>
              {entries.length === 0 ? (
                <Typography variant='body2' color='text.secondary'>
                  Keine Eintragungen in Abteilung III.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {entries.map((e, eIdx) => {
                    const eId = e.idGrundbuchAbteilung3 as number;
                    return (
                      <Paper
                        key={eId}
                        variant='outlined'
                        sx={{
                          p: 1.5,
                          bgcolor: 'action.hover',
                          opacity: e.istGeloescht ? 0.6 : 1,
                        }}
                      >
                        <Box
                          sx={{ display: 'flex', justifyContent: 'flex-end' }}
                        >
                          {renderMoveArrows(eIdx, entries.length, dir =>
                            moveInGrundstueckList(
                              gsId,
                              'Abteilung3Array',
                              eIdx,
                              dir
                            )
                          )}
                        </Box>
                        <Stack
                          direction='row'
                          spacing={1.5}
                          flexWrap='wrap'
                          useFlexGap
                          alignItems='flex-start'
                        >
                          <TextField
                            select
                            size='small'
                            label='Grundstück'
                            value={gsId}
                            onChange={ev =>
                              moveAbt3(gsId, eId, Number(ev.target.value))
                            }
                            sx={{ width: 120 }}
                          >
                            {grundstueckOptions.map(g => (
                              <MenuItem key={g.id} value={g.id}>
                                Lfd. Nr. {g.lfdNr}
                              </MenuItem>
                            ))}
                          </TextField>
                          <TextField
                            size='small'
                            label='Lfd. Nr.'
                            value={e.lfdNr ?? ''}
                            onChange={ev =>
                              updateAbt3(gsId, eId, 'lfdNr', ev.target.value)
                            }
                            sx={{ width: 80 }}
                          />
                          <TextField
                            size='small'
                            label='Belastungsart'
                            placeholder='z. B. Grundschuld'
                            value={e.Belastungsart ?? ''}
                            onChange={ev =>
                              updateAbt3(
                                gsId,
                                eId,
                                'Belastungsart',
                                ev.target.value
                              )
                            }
                            sx={{ flexGrow: 1, minWidth: 220 }}
                          />
                          <TextField
                            size='small'
                            label='Betrag'
                            type='number'
                            value={e.Betrag ?? ''}
                            onChange={ev =>
                              updateAbt3(
                                gsId,
                                eId,
                                'Betrag',
                                ev.target.value === ''
                                  ? null
                                  : Number(ev.target.value)
                              )
                            }
                            sx={{ width: 130 }}
                          />
                          <TextField
                            select
                            size='small'
                            label='Währung'
                            value={e.Waehrung ?? 'EUR'}
                            onChange={ev =>
                              updateAbt3(gsId, eId, 'Waehrung', ev.target.value)
                            }
                            sx={{ width: 100 }}
                          >
                            <MenuItem value='EUR'>EUR</MenuItem>
                            <MenuItem value='DM'>DM</MenuItem>
                          </TextField>
                          <TextField
                            size='small'
                            label='Gläubiger'
                            value={e.Glaeubiger ?? ''}
                            onChange={ev =>
                              updateAbt3(
                                gsId,
                                eId,
                                'Glaeubiger',
                                ev.target.value
                              )
                            }
                            sx={{ flexGrow: 1, minWidth: 220 }}
                          />
                          <TextField
                            size='small'
                            label='Zins %'
                            type='number'
                            value={e.Zinssatz ?? ''}
                            onChange={ev =>
                              updateAbt3(
                                gsId,
                                eId,
                                'Zinssatz',
                                ev.target.value === ''
                                  ? null
                                  : Number(ev.target.value)
                              )
                            }
                            sx={{ width: 90 }}
                          />
                          <TextField
                            size='small'
                            label='Eingetragen am'
                            type='date'
                            InputLabelProps={{ shrink: true }}
                            value={e.eingetragenAm ?? ''}
                            onChange={ev =>
                              updateAbt3(
                                gsId,
                                eId,
                                'eingetragenAm',
                                ev.target.value
                              )
                            }
                            sx={{ width: 160 }}
                          />
                          <FormControlLabel
                            sx={{ mt: 0.5 }}
                            control={
                              <Checkbox
                                size='small'
                                checked={!!e.isOhneBrief}
                                onChange={ev =>
                                  updateAbt3(
                                    gsId,
                                    eId,
                                    'isOhneBrief',
                                    ev.target.checked
                                  )
                                }
                              />
                            }
                            label='ohne Brief'
                          />
                          <FormControlLabel
                            sx={{ mt: 0.5 }}
                            control={
                              <Checkbox
                                size='small'
                                checked={!!e.isVollstreckbar}
                                onChange={ev =>
                                  updateAbt3(
                                    gsId,
                                    eId,
                                    'isVollstreckbar',
                                    ev.target.checked
                                  )
                                }
                              />
                            }
                            label='vollstreckbar'
                          />
                          <Box sx={{ flexGrow: 1 }} />
                          <Tooltip title='1:1-Details (Volltext, Rang, Gesamthaft, Nebenleistung, Veränderungen, Rechtspfleger, Löschung)'>
                            <IconButton
                              size='small'
                              onClick={() => toggleAbt(`3-${eId}`)}
                            >
                              <ExpandMore
                                fontSize='small'
                                sx={{
                                  transform: abtExpanded[`3-${eId}`]
                                    ? 'rotate(180deg)'
                                    : 'none',
                                  transition: '0.2s',
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title='Eintrag entfernen'>
                            <IconButton
                              size='small'
                              onClick={() => removeAbt3(gsId, eId)}
                            >
                              <DeleteOutline fontSize='small' />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        <Collapse in={!!abtExpanded[`3-${eId}`]} unmountOnExit>
                          <Stack spacing={1} sx={{ pt: 1.5 }}>
                            <TextField
                              size='small'
                              fullWidth
                              multiline
                              label='Volltext (wörtlich, Spalte 4)'
                              value={e.Volltext ?? ''}
                              onChange={ev =>
                                updateAbt3(
                                  gsId,
                                  eId,
                                  'Volltext',
                                  ev.target.value
                                )
                              }
                            />
                            <Stack
                              direction='row'
                              spacing={1}
                              flexWrap='wrap'
                              useFlexGap
                            >
                              <TextField
                                size='small'
                                label='Rangvermerk'
                                placeholder='im Rang vor/nach …'
                                value={e.Rangvermerk ?? ''}
                                onChange={ev =>
                                  updateAbt3(
                                    gsId,
                                    eId,
                                    'Rangvermerk',
                                    ev.target.value
                                  )
                                }
                                sx={{ flexGrow: 1, minWidth: 200 }}
                              />
                              <TextField
                                size='small'
                                label='Nebenleistung'
                                placeholder='z. B. 5% einmalig'
                                value={e.Nebenleistung ?? ''}
                                onChange={ev =>
                                  updateAbt3(
                                    gsId,
                                    eId,
                                    'Nebenleistung',
                                    ev.target.value
                                  )
                                }
                                sx={{ width: 170 }}
                              />
                              <TextField
                                size='small'
                                label='Gesamthaft (Blätter)'
                                placeholder='z. B. 10554–10573'
                                value={e.GesamthaftBlaetter ?? ''}
                                onChange={ev =>
                                  updateAbt3(
                                    gsId,
                                    eId,
                                    'GesamthaftBlaetter',
                                    ev.target.value
                                  )
                                }
                                sx={{ width: 200 }}
                              />
                            </Stack>
                            <TextField
                              size='small'
                              fullWidth
                              multiline
                              label='Veränderungen (Abtretung, Umstellung DM→EUR, Mithaft-Entlassung …)'
                              value={e.Veraenderungen ?? ''}
                              onChange={ev =>
                                updateAbt3(
                                  gsId,
                                  eId,
                                  'Veraenderungen',
                                  ev.target.value
                                )
                              }
                            />
                            <TextField
                              size='small'
                              fullWidth
                              label='Rechtspfleger (Unterzeichner)'
                              value={e.Rechtspfleger ?? ''}
                              onChange={ev =>
                                updateAbt3(
                                  gsId,
                                  eId,
                                  'Rechtspfleger',
                                  ev.target.value
                                )
                              }
                            />
                            <Stack
                              direction='row'
                              spacing={1}
                              alignItems='center'
                            >
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size='small'
                                    checked={!!e.istGeloescht}
                                    onChange={ev =>
                                      updateAbt3(
                                        gsId,
                                        eId,
                                        'istGeloescht',
                                        ev.target.checked
                                      )
                                    }
                                  />
                                }
                                label='gelöscht (historisch)'
                              />
                              {e.istGeloescht ? (
                                <TextField
                                  size='small'
                                  type='date'
                                  label='Gelöscht am'
                                  InputLabelProps={{ shrink: true }}
                                  value={e.geloeschtAm ?? ''}
                                  onChange={ev =>
                                    updateAbt3(
                                      gsId,
                                      eId,
                                      'geloeschtAm',
                                      ev.target.value
                                    )
                                  }
                                  sx={{ width: 180 }}
                                />
                              ) : null}
                            </Stack>
                          </Stack>
                        </Collapse>
                      </Paper>
                    );
                  })}
                </Stack>
              )}
            </Paper>
          );
        })
      )}
    </Stack>
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth='xl' fullWidth>
        <DialogTitle>
          {isEdit
            ? `Grundbuchblatt ${blattFullObject?.Blattnummer ?? ''} bearbeiten`
            : 'Neues Grundbuchblatt'}
        </DialogTitle>
        <DialogContent dividers>
          {isEdit && isLoadingBlattFullObject ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              {error && <Alert severity='error'>{error}</Alert>}

              {/* Blatt-Kopf (über allen Tabs) */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label='Blattnummer'
                  required
                  value={blattnummer}
                  onChange={e => setBlattnummer(e.target.value)}
                  size='small'
                  sx={{ flex: 1 }}
                />
                <TextField
                  select
                  label='Amtsgericht'
                  value={idCourt}
                  onChange={e => {
                    setIdCourt(e.target.value ? Number(e.target.value) : '');
                    setIdAdministrativeUnit('');
                  }}
                  size='small'
                  sx={{ flex: 1 }}
                >
                  <MenuItem value=''>—</MenuItem>
                  {(courts ?? []).map(c => (
                    <MenuItem key={c.idCourt} value={c.idCourt}>
                      {c.Court}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label='Gemarkung'
                  value={idAdministrativeUnit}
                  onChange={e =>
                    setIdAdministrativeUnit(
                      e.target.value ? Number(e.target.value) : ''
                    )
                  }
                  size='small'
                  sx={{ flex: 1 }}
                >
                  <MenuItem value=''>—</MenuItem>
                  {gemarkungOptions.map(u => (
                    <MenuItem
                      key={u.idAdministrativeUnit}
                      value={u.idAdministrativeUnit}
                    >
                      {u.AdministrativeUnit}
                    </MenuItem>
                  ))}
                </TextField>
                {idAdministrativeUnit === '' && (
                  <TextField
                    label='Gemarkung (Freitext)'
                    helperText='falls nicht in der Liste'
                    value={gemarkungFreitext}
                    onChange={e => setGemarkungFreitext(e.target.value)}
                    size='small'
                    sx={{ flex: 1 }}
                  />
                )}
              </Stack>

              {/* Aufschrift: Blatt-Typ + Fortführungs-/Schließungs-/Freigabevermerke */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  select
                  label='Blattart'
                  value={blattart}
                  onChange={e => setBlattart(e.target.value)}
                  size='small'
                  sx={{ flex: 1 }}
                >
                  <MenuItem value=''>—</MenuItem>
                  <MenuItem value='Grundbuch'>Grundbuch</MenuItem>
                  <MenuItem value='Wohnungsgrundbuch'>
                    Wohnungsgrundbuch
                  </MenuItem>
                  <MenuItem value='Teileigentumsgrundbuch'>
                    Teileigentumsgrundbuch
                  </MenuItem>
                  <MenuItem value='Erbbaugrundbuch'>Erbbaugrundbuch</MenuItem>
                  <MenuItem value='Wohnungserbbaugrundbuch'>
                    Wohnungserbbaugrundbuch
                  </MenuItem>
                  <MenuItem value='Teilerbbaugrundbuch'>
                    Teilerbbaugrundbuch
                  </MenuItem>
                </TextField>
                <TextField
                  label='Aufschriftvermerke (Fortführung / Schließung / Freigabe)'
                  value={aufschriftvermerke}
                  onChange={e => setAufschriftvermerke(e.target.value)}
                  size='small'
                  multiline
                  minRows={1}
                  sx={{ flex: 3 }}
                />
              </Stack>

              <TabContext value={tab}>
                <Box
                  sx={{
                    display: 'flex',
                    borderTop: 1,
                    borderColor: 'divider',
                    mt: 1,
                  }}
                >
                  <Box
                    sx={{
                      borderRight: 1,
                      borderColor: 'divider',
                      minWidth: 210,
                      flexShrink: 0,
                    }}
                  >
                    <TabList
                      orientation='vertical'
                      onChange={(_, v) => setTab(v as TabKey)}
                      variant='scrollable'
                      scrollButtons='auto'
                      allowScrollButtonsMobile
                      sx={{
                        '& .MuiTab-root': {
                          alignItems: 'flex-start',
                          justifyContent: 'flex-start',
                          textAlign: 'left',
                          minHeight: 44,
                        },
                      }}
                    >
                      <Tab
                        label={`Bestandsverzeichnis (${flurstueckGesamt})`}
                        value='bv'
                      />
                      <Tab
                        label={`Abteilung I (${owners.length})`}
                        value='abt1'
                      />
                      <Tab
                        label={`Abteilung II (${abt2Gesamt})`}
                        value='abt2'
                      />
                      <Tab
                        label={`Abteilung III (${abt3Gesamt})`}
                        value='abt3'
                      />
                      <Tab
                        label={`Abrufe (${blattFullObject?.Abrufe?.length ?? 0})`}
                        value='abrufe'
                      />
                    </TabList>
                  </Box>
                  <Box sx={{ flexGrow: 1, minWidth: 0, pl: 2 }}>
                    {/* Bestandsverzeichnis: Grundstücke -> Flurstücke + Adressen */}
                    <TabPanel value='bv' sx={{ px: 0 }}>
                      <Stack spacing={2}>
                        <Stack direction='row' alignItems='center' spacing={1}>
                          <Typography variant='subtitle2'>
                            Grundstücke (Bestandsverzeichnis)
                          </Typography>
                          <Chip
                            size='small'
                            label={grundstuecke.length}
                            variant='outlined'
                          />
                          <Box sx={{ flexGrow: 1 }} />
                          <Button
                            size='small'
                            variant='outlined'
                            startIcon={<Add />}
                            onClick={addGrundstueck}
                          >
                            Grundstück
                          </Button>
                        </Stack>
                        {grundstuecke.length === 0 ? (
                          <Typography variant='body2' color='text.secondary'>
                            Noch kein Grundstück. Über „Grundstück" hinzufügen.
                          </Typography>
                        ) : (
                          grundstuecke.map(renderGrundstueck)
                        )}
                      </Stack>
                    </TabPanel>

                    {/* Abteilung I: Eigentümer */}
                    <TabPanel value='abt1' sx={{ px: 0 }}>
                      <BlattOwnersEditor
                        owners={owners}
                        setOwners={setOwners}
                        grundstueckOptions={grundstueckOptions}
                      />
                    </TabPanel>

                    {/* Abteilung II: Lasten und Beschränkungen */}
                    <TabPanel value='abt2' sx={{ px: 0 }}>
                      {renderAbteilung2()}
                    </TabPanel>
                    {/* Abteilung III: Grundpfandrechte */}
                    <TabPanel value='abt3' sx={{ px: 0 }}>
                      {renderAbteilung3()}
                    </TabPanel>
                    <TabPanel value='abrufe' sx={{ px: 0 }}>
                      <Stack
                        direction='row'
                        alignItems='center'
                        spacing={1}
                        sx={{ mb: 1 }}
                      >
                        <Typography variant='subtitle2'>
                          Abrufe (Grundbuchauszug, Aktualitätsnachweis,
                          Markentabelle)
                        </Typography>
                        <Box sx={{ flexGrow: 1 }} />
                        <Tooltip
                          title={
                            isEdit
                              ? ''
                              : 'Bitte das Blatt zuerst speichern, dann ist ein Abruf möglich.'
                          }
                        >
                          <span>
                            <Button
                              size='small'
                              variant='contained'
                              startIcon={<TravelExplore />}
                              disabled={!isEdit}
                              onClick={() => setIsOpenAbruf(true)}
                            >
                              Neuen Abruf durchführen
                            </Button>
                          </span>
                        </Tooltip>
                      </Stack>
                      {(blattFullObject?.Abrufe ?? []).length === 0 ? (
                        <Typography variant='body2' color='text.secondary'>
                          Noch keine Abrufe zu diesem Blatt.
                        </Typography>
                      ) : (
                        <Table size='small'>
                          <TableHead>
                            <TableRow>
                              <TableCell>Datum</TableCell>
                              <TableCell>Dokument</TableCell>
                              <TableCell>Aktualitätsnachweis</TableCell>
                              <TableCell>Markentabelle</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(blattFullObject?.Abrufe ?? []).map(q => (
                              <TableRow
                                key={q.idRealEstateLandRegisterEntryQuery}
                              >
                                <TableCell>{q.Created_at ?? '–'}</TableCell>
                                <TableCell>{q.FileName ?? '–'}</TableCell>
                                <TableCell>{q.ActualityProof ?? '–'}</TableCell>
                                <TableCell>
                                  {q.ListOfPending ? 'vorhanden' : '–'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </TabPanel>
                  </Box>
                </Box>
              </TabContext>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSavingBlatt}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            onClick={handleSave}
            disabled={isSavingBlatt}
            startIcon={
              isSavingBlatt ? <CircularProgress size={16} /> : undefined
            }
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
      {isOpenAbruf && isEdit && (
        <BlattAbrufDialog
          open={isOpenAbruf}
          onClose={() => setIsOpenAbruf(false)}
          idGrundbuchblatt={idGrundbuchblatt!}
          blattnummer={blattnummer}
          idCourt={idCourt === '' ? null : idCourt}
          onDone={() => refetchBlattFullObject()}
        />
      )}
    </>
  );
};
